/* ============================================================
   BOOST — halaman pelanggan  (SESAAT Studio v3.9)
   ============================================================
   PLAN_BOOST.md §🔄 [2026-11-04] — MCN pertama: Gorilla Cat.

   Aliran pelanggan:
     1. Nampak kad MCN → setiap MCN ada program (TikTok TAP · Shopee Bind · TikTok Bind).
     2. Apply → popup profil (diisi sekali, diguna semula) + tick setuju kongsi data.
     3. Auto approve (ikut tetapan program) → link group WhatsApp + page program.
     4. Senarai seller/produk = KOSONG dahulu (MCN sedang sediakan; CSV / API = fasa 2).
   Had: Bind = 1 MCN setiap platform (server menyekat; di sini butang diganti "Bound to X").

   ⛔ Semua data melalui callable `boost` (services/boostService.js) — halaman ini
      TIDAK membaca/menulis Firestore terus. Kelayakan pakej disemak di SERVER.
   ⛔ Siapa nampak halaman ini: pagar `isAdmin || config/boost.open` dalam App.jsx +
      StudioShell (useBoostBuka.js). `adminSahaja` = admin melihat sebelum suis dibuka.
   Teks UI → kamus boost.* (en.js + ms.js) — jangan taip ayat terus.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Rocket, Lock, ArrowLeft, MessageCircle, Menu, Music2, ShoppingBag, Store, UserRound, Loader2, Check,
} from 'lucide-react';

import '../design/tokens.css';
import { PageHeader, Card, CardBody, Btn, Badge, EmptyState } from '../design/ui';
import { useT } from '../i18n';
import { panggilBoost, mesejRalatPelanggan, JENIS } from '../services/boostService';
import BoostMohonModal from './BoostMohonModal';

/* Kunci kamus, bukan ayat — ayatnya dalam en.js / ms.js. */
const LANGKAH = ['boost.step1', 'boost.step2', 'boost.step3'];

const IkonPlatform = ({ platform, size = 16 }) => (platform === 'shopee'
    ? <ShoppingBag size={size} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
    : <Music2 size={size} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />);

export default function BoostV39View({ onMenuClick, adminSahaja = false }) {
    const t = useT();
    const navigate = useNavigate();

    const [data, setData] = useState(null);       // jawapan `muat`
    const [ralatMuat, setRalatMuat] = useState('');
    const [popup, setPopup] = useState(null);     // { program, mcn } | { profil: true }
    const [buka, setBuka] = useState(null);       // { mcnId, programId } — page program
    const [disimpan, setDisimpan] = useState(false);

    /* `.then`, bukan async/await: lint React (set-state-in-effect) hanya menerima setState dalam callback. */
    const muat = useCallback(() => panggilBoost('muat').then(
        (h) => { setData(h); setRalatMuat(''); },
        (e) => setRalatMuat(mesejRalatPelanggan(e, t)),
    ), [t]);
    useEffect(() => { muat(); }, [muat]);

    const mcnBuka = buka && data?.mcn.find((m) => m.id === buka.mcnId);
    const programBuka = mcnBuka?.program.find((p) => p.id === buka.programId);

    async function selepasPopup(hasil) {
        const p = popup;
        setPopup(null);
        if (p?.profil) {
            setDisimpan(true);
            setTimeout(() => setDisimpan(false), 1800);
        }
        await muat();
        // Terus ke page program bila lulus — itu yang pelanggan mahu lihat seterusnya.
        if (p?.program && hasil?.status === 'lulus') setBuka({ mcnId: p.mcn.id, programId: p.program.id });
    }

    /* ── Kepala ──────────────────────────────────────────────── */
    const kepala = (
        <>
            {onMenuClick && (
                <div className="md:hidden" style={{ marginBottom: 12 }}>
                    <button
                        onClick={onMenuClick} aria-label={t('boost.openMenu')}
                        style={{
                            background: 'transparent', border: 0, padding: 6, borderRadius: 8,
                            color: 'var(--s-ink-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                        }}
                    >
                        <Menu size={20} strokeWidth={1.9} />
                    </button>
                </div>
            )}
            <PageHeader
                title="Boost"
                subtitle={t('boost.subtitle')}
                sisiTajuk={<Rocket size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)' }} />}
                actions={adminSahaja ? <Badge tone="warn">{t('boost.adminNotOpen')}</Badge> : null}
            />
        </>
    );

    /* ── Memuat / ralat ──────────────────────────────────────── */
    let isi;
    if (ralatMuat) {
        isi = (
            <EmptyState title={t('boost.err.load')} action={<Btn variant="ghost" onClick={() => { setRalatMuat(''); setData(null); muat(); }}>{t('boost.retry')}</Btn>}>
                {ralatMuat}
            </EmptyState>
        );
    } else if (!data) {
        isi = (
            <div className="s-dim" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '24px 0' }}>
                <Loader2 size={16} strokeWidth={2} className="animate-spin" /> {t('boost.loading')}
            </div>
        );
    } else if (!data.layak) {
        /* ── Tiada pakej yang layak ──────────────────────────── */
        isi = (
            <Card>
                <CardBody style={{ textAlign: 'center', padding: '44px 28px' }}>
                    <div style={{
                        width: 46, height: 46, borderRadius: 999, margin: '0 auto 16px',
                        display: 'grid', placeItems: 'center',
                        background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                    }}>
                        <Lock size={20} strokeWidth={1.8} style={{ color: 'var(--s-ink-2)' }} />
                    </div>
                    <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--s-ink)', margin: '0 0 8px' }}>
                        {t('boost.locked.title')}
                    </h2>
                    <p className="s-dim" style={{ maxWidth: 460, margin: '0 auto 20px', lineHeight: 1.6 }}>
                        {t('boost.locked.desc')}
                    </p>
                    <Btn variant="primary" onClick={() => navigate('/pricing')}>{t('boost.locked.cta')}</Btn>
                </CardBody>
            </Card>
        );
    } else if (mcnBuka && programBuka) {
        /* ── Page satu program (selepas lulus) ───────────────── */
        const st = programBuka.permohonan?.status;
        isi = (
            <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    <Btn variant="quiet" size="sm" icon={<ArrowLeft size={14} strokeWidth={2} />} onClick={() => setBuka(null)}>
                        {t('boost.back')}
                    </Btn>
                    <IkonPlatform platform={programBuka.platform} />
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>
                        {mcnBuka.nama} · {programBuka.label}
                    </span>
                    {st === 'lulus' && <Badge tone="ok">{t('boost.status.approved')}</Badge>}
                    <div style={{ flex: 1 }} />
                    {programBuka.waLink && (
                        <Btn variant="ghost" size="sm" icon={<MessageCircle size={14} strokeWidth={1.9} />}
                            onClick={() => window.open(programBuka.waLink, '_blank', 'noopener')}>
                            {t('boost.joinGroup')}
                        </Btn>
                    )}
                </div>

                {/* 3 langkah showcase = aliran TikTok sahaja. Aliran Shopee Bind belum ditentukan GC — tidak diteka. */}
                {programBuka.platform === 'tiktok' && <Card style={{ marginBottom: 16 }}>
                    <CardBody>
                        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', margin: '0 0 2px' }}>{t('boost.howTitle')}</h2>
                        {LANGKAH.map((kunci, i) => (
                            <div className="s-step" key={kunci}>
                                <span className="s-step__no">{i + 1}</span>
                                <span style={{ fontSize: 13.5, color: 'var(--s-ink-2)', lineHeight: 1.55, alignSelf: 'center' }}>{t(kunci)}</span>
                            </div>
                        ))}
                    </CardBody>
                </Card>}

                <Card>
                    <CardBody>
                        <div style={{ textAlign: 'center', padding: '28px 12px' }}>
                            <Store size={22} strokeWidth={1.7} style={{ color: 'var(--s-ink-3)' }} />
                            <p style={{ margin: '10px 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{t('boost.catalog.title')}</p>
                            <p className="s-dim" style={{ margin: '0 auto', maxWidth: 440, lineHeight: 1.55 }}>
                                {t('boost.catalog.desc', { mcn: mcnBuka.nama })}
                            </p>
                        </div>
                    </CardBody>
                </Card>
            </>
        );
    } else {
        /* ── Senarai MCN + program ───────────────────────────── */
        const profil = data.profil;
        isi = (
            <>
                <Card style={{ marginBottom: 16 }}>
                    <CardBody style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <UserRound size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{t('boost.profile.title')}</p>
                            <p className="s-dim" style={{ margin: '2px 0 0', lineHeight: 1.5 }}>
                                {profil
                                    ? [profil.nama, profil.wa, profil.tiktok1 && `@${profil.tiktok1}`, profil.shopee && `Shopee: ${profil.shopee}`].filter(Boolean).join(' · ')
                                    : t('boost.profile.empty')}
                            </p>
                        </div>
                        {disimpan && <span className="s-dim" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={14} strokeWidth={2.2} /> {t('boost.profile.saved')}</span>}
                        <Btn variant="ghost" size="sm" onClick={() => setPopup({ profil: true })}>{t('boost.profile.edit')}</Btn>
                    </CardBody>
                </Card>

                {data.mcn.length === 0 ? (
                    <EmptyState title={t('boost.noPrograms')}>{t('boost.noProgramsDesc')}</EmptyState>
                ) : (
                    <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))' }}>
                        {data.mcn.map((m) => (
                            <Card key={m.id}>
                                <CardBody>
                                    <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>{m.nama}</p>
                                    {m.penerangan && <p className="s-dim" style={{ margin: '4px 0 0', lineHeight: 1.55 }}>{m.penerangan}</p>}
                                    <div style={{ marginTop: 12 }}>
                                        {m.program.map((p) => (
                                            <BarisProgram key={p.id} p={p} t={t}
                                                onApply={() => setPopup({ program: p, mcn: m })}
                                                onBuka={() => setBuka({ mcnId: m.id, programId: p.id })} />
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            {kepala}
            {isi}
            {popup && (
                <BoostMohonModal
                    program={popup.program || null}
                    mcnNama={popup.mcn?.nama || ''}
                    emel={data?.emel}
                    profilAwal={data?.profil}
                    onTutup={() => setPopup(null)}
                    onSiap={selepasPopup}
                />
            )}
        </div>
    );
}

/* Satu baris program dalam kad MCN. Butang ikut keadaan — tiada butang yang tidak buat apa-apa. */
function BarisProgram({ p, t, onApply, onBuka }) {
    const st = p.permohonan?.status;
    let kanan;
    if (st === 'lulus') {
        kanan = (
            <>
                <Badge tone="ok">{t('boost.status.approved')}</Badge>
                <Btn size="sm" onClick={onBuka}>{t('boost.openProgram')}</Btn>
            </>
        );
    } else if (st === 'menunggu') {
        kanan = <Badge tone="warn">{t('boost.status.pending')}</Badge>;
    } else if (st === 'ditolak') {
        kanan = <Badge tone="stop">{t('boost.status.declined')}</Badge>;
    } else if (p.status === 'segera') {
        kanan = <span className="s-dim">{t('boost.soon')}</span>;
    } else if (p.sekatBind) {
        kanan = <span className="s-dim">{t('boost.boundTo', { mcn: p.sekatBind.mcnNama })}</span>;
    } else {
        kanan = <Btn size="sm" onClick={onApply}>{t('boost.apply')}</Btn>;
    }
    const segera = p.status === 'segera' && !st;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderTop: '1px solid var(--s-line)', flexWrap: 'wrap' }}>
            <IkonPlatform platform={JENIS[p.jenis]?.platform} />
            <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: segera ? 'var(--s-ink-3)' : 'var(--s-ink)' }}>{p.label}</span>
                    {!segera && <Badge tone={p.bind ? 'warn' : 'ok'}>{p.bind ? t('boost.tag.one') : t('boost.tag.many')}</Badge>}
                </div>
                {p.penerangan && !segera && <p className="s-dim" style={{ margin: '2px 0 0', lineHeight: 1.5 }}>{p.penerangan}</p>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{kanan}</div>
        </div>
    );
}
